import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useEffect, useState } from 'react';
import { Button, makeStyles, Spinner, Text, tokens } from '@fluentui/react-components';
import { AddCircle20Regular, AddCircleRegular, Money20Regular } from '@fluentui/react-icons';
import { Column, PortalSlotContent, Row } from '../../../common';
import { Case, Credit, Credits } from '../../../../data/types';
import { useCreditsService } from '../../../../data/services/useCreditsService';
import { useUtilities } from '../../../../utilities/useUtilities';
import {
    CustomDataTable,
    CustomDataTableColumn,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { AddPartyCreditDialog } from '../../../dialogs/AddPartyCreditDialog';
import { useToasts } from '../../../toasts/useToasts';

import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { appTokens } from '../../../common/Styles/appTokens';

const useStyles = makeStyles({
    summaryCard: {
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        padding: '0.75rem 1rem',
        minWidth: '10rem',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    summaryLabel: {
        color: tokens.colorNeutralForeground3,
    },
    summaryValue: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase500,
    },
    redeemedValue: {
        color: appTokens.text.colors.danger,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase500,
    },
    availableValue: {
        color: appTokens.text.colors.success,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase500,
    },
    balancePositive: {
        color: tokens.colorPaletteGreenForeground1,
    },
    breakdown: {
        marginTop: '0.5rem',
        paddingTop: '0.5rem',
        borderTop: `1px dashed ${tokens.colorNeutralStroke2}`,
    },
    breakdownLabel: {
        color: appTokens.text.colors.brand,
        fontSize: tokens.fontSizeBase200,
    },
    breakdownValue: {
        color: appTokens.text.colors.default,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
    },
    empty: {
        padding: '1rem',
        color: tokens.colorNeutralForeground3,
    },
});

enum ColumnName {
    dateIssued = 'dateIssued',
    description = 'description',
    amount = 'amount',
    issuedBy = 'issuedBy',
}

export const CaseCreditsPage = () => {
    const { caseForm } = useCaseServicesContext()!;
    const caseObj = caseForm.values as Case;
    const clientParty = caseObj?.retainedBy?.party;

    //Hooks
    const creditsService = useCreditsService();
    const utils = useUtilities();
    const toasts = useToasts();
    const { getUserName } = useAccountState();

    //State
    const [credits, setCredits] = useState<Credits | undefined>();
    const [loading, setLoading] = useState<boolean>(false);
    const [addOpen, setAddOpen] = useState<boolean>(false);

    //Styles
    const styles = useStyles();

    //Data
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!clientParty?.partyId) return;
            setLoading(true);
            try {
                const result = await creditsService.getCreditsForPartyAsync(clientParty.partyId);
                if (!cancelled) setCredits(result);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientParty?.partyId]);

    const reloadCredits = async () => {
        if (!clientParty?.partyId) return;
        setLoading(true);
        try {
            const result = await creditsService.getCreditsForPartyAsync(clientParty.partyId);
            setCredits(result);
        } finally {
            setLoading(false);
        }
    };
    const onAddCredit = async (credit: Credit) => {
        if (!clientParty?.partyId) return;
        try {
            const result = await creditsService.addCreditAsync(clientParty.partyId, credit);
            if (result) setCredits(result);
            else await reloadCredits();
            toasts.notifySuccess(
                'Credit Added',
                `A credit of ${utils.formatCurrency(credit.amount)} was added to ${caseObj?.caseName ?? 'this case'}.`,
            );
        } catch (err) {
            console.error('Add case credit failed', err);
            toasts.notifyError('Add Credit Failed', `Unable to add credit to this case. ${(err as Error)?.message ?? ''}`);
        }
    };

    //Case-specific credits only
    const caseCredits = (credits?.creditsList ?? []).filter((c) => c.caseId === caseObj?.caseId);
    const caseRedemptions = (credits?.redeemedCreditsList ?? []).filter((r) => r.caseId === caseObj?.caseId);

    //Summary
    const totalIssued = caseCredits.reduce((sum, c) => sum + (c.amount ?? 0), 0);
    const totalRedeemed = caseRedemptions.reduce((sum, r) => sum + (r.amount ?? 0), 0);
    const balance = totalIssued - totalRedeemed;
    const partyIssued = (credits?.creditsList ?? []).reduce((sum, c) => sum + (c.amount ?? 0), 0);
    const partyRedeemed = (credits?.redeemedCreditsList ?? []).reduce((sum, r) => sum + (r.amount ?? 0), 0);
    const nonCaseIssued = (credits?.creditsList ?? []).reduce((sum, c) => sum + (!c.caseId ? (c.amount ?? 0) : 0), 0);
    const nonCaseRedeemed = (credits?.redeemedCreditsList ?? []).reduce(
        (sum, r) => sum + (!r.caseId ? (r.amount ?? 0) : 0),
        0,
    );
    const nonCaseBalance = nonCaseIssued - nonCaseRedeemed;

    //Columns
    const columns: CustomDataTableColumn<Credit>[] = [
        {
            id: ColumnName.dateIssued,
            header: 'Date Issued',
            compare: (a, b) => utils.date.compareDates(a.dateIssued as unknown as Date, b.dateIssued as unknown as Date),
            renderCell: (c) => (
                <>
                    <Text>{c.dateIssued ? utils.date.formatDate(new Date(c.dateIssued), undefined, true) : ''}</Text>
                </>
            ),
            defaultWidth: 140,
            minWidth: 110,
        },
        {
            id: ColumnName.description,
            header: 'Description',
            compare: (a, b) => (a.description ?? '').localeCompare(b.description ?? ''),
            renderCell: (c) => (
                <>
                    <Text>{c.description}</Text>
                </>
            ),
            defaultWidth: 320,
            minWidth: 180,
        },
        {
            id: ColumnName.amount,
            header: 'Amount',
            compare: (a, b) => (a.amount ?? 0) - (b.amount ?? 0),
            renderCell: (c) => (
                <>
                    <Text weight='semibold'>{utils.formatCurrency(c.amount)}</Text>
                </>
            ),
            justifyContent: 'start',
            defaultWidth: 120,
            minWidth: 100,
        },
        {
            id: ColumnName.issuedBy,
            header: 'Issued By',
            compare: (a, b) => (a.issuedBy ?? '').localeCompare(b.issuedBy ?? ''),
            renderCell: (c) => (
                <>
                    <Text>{getUserName(c.issuedBy || '')}</Text>
                </>
            ),
            defaultWidth: 160,
            minWidth: 120,
        },
    ];

    if (!clientParty?.partyId) {
        return (
            <Column gap='medium' flex='1'>
                <div className={styles.empty} data-testid='case-credits-no-client'>
                    A client must be assigned to this case before credits can be tracked.
                </div>
            </Column>
        );
    }

    return (
        <>
            <PortalSlotContent name='invoicesPageHeader'>
                <Column gap='medium' flex='1'>
                    <AddPartyCreditDialog
                        title='Add Case Credit'
                        caseId={caseObj?.caseId}
                        open={addOpen}
                        onOpenChange={(o: boolean) => setAddOpen(o)}
                        onSubmit={onAddCredit}
                    >
                        <Button
                            icon={<AddCircleRegular />}
                            onClick={() => setAddOpen(true)}
                            disabled={!caseObj?.caseId}
                            data-testid='case-credits-add-button'
                        >
                            Add Credit
                        </Button>
                    </AddPartyCreditDialog>
                </Column>
            </PortalSlotContent>
            <Column>
                <Row gap='medium'>
                    <Column width='auto' className={styles.summaryCard}>
                        <Row gap='small'>
                            <Money20Regular />
                            <Text className={styles.summaryLabel}>Issued to Case</Text>
                        </Row>
                        <Text className={styles.summaryValue}>{utils.formatCurrency(totalIssued)}</Text>
                        {/* <Column gap='small' className={styles.breakdown}>
                        <Row justifyContent='space-between' gap='medium'>
                            <Text className={styles.breakdownLabel}>{clientParty?.name ?? 'Client'} Total</Text>
                            <Text className={styles.breakdownValue}>{utils.formatCurrency(partyIssued)}</Text>
                        </Row>
                    </Column> */}
                    </Column>
                    <Column width='auto' className={styles.summaryCard}>
                        <Text className={styles.summaryLabel}>Redeemed on Case</Text>
                        <Text className={styles.redeemedValue}>{utils.formatCurrency(totalRedeemed)}</Text>
                        {/* <Column gap='small' className={styles.breakdown}>
                        <Row justifyContent='space-between' gap='medium'>
                            <Text className={styles.breakdownLabel}>{clientParty?.name ?? 'Client'} Total</Text>
                            <Text className={styles.breakdownValue}>{utils.formatCurrency(partyRedeemed)}</Text>
                        </Row>
                    </Column> */}
                    </Column>
                    <Column width='auto' className={styles.summaryCard}>
                        <Text className={styles.summaryLabel}>Available Balance</Text>
                        <Text
                            className={`${styles.availableValue} ${balance + nonCaseBalance > 0 ? styles.balancePositive : ''}`}
                        >
                            {utils.formatCurrency(balance + nonCaseBalance)}
                        </Text>
                        <Column gap='small' className={styles.breakdown}>
                            <Row justifyContent='space-between' gap='medium'>
                                <Text className={styles.breakdownLabel}>Available to Case</Text>
                                <Text className={styles.breakdownValue}>{utils.formatCurrency(balance)}</Text>
                            </Row>
                            <Row justifyContent='space-between' gap='medium'>
                                <Text className={styles.breakdownLabel}>Available to Client</Text>
                                <Text className={styles.breakdownValue}>{utils.formatCurrency(nonCaseBalance)}</Text>
                            </Row>
                        </Column>
                    </Column>
                </Row>
                {loading ? (
                    <Row>
                        <Spinner label='Loading credits...' size='small' />
                    </Row>
                ) : caseCredits.length === 0 ? (
                    <div className={styles.empty} data-testid='case-credits-empty'>
                        No credits found for this case.
                    </div>
                ) : (
                    <CustomDataTable<Credit> items={caseCredits} rowId='creditId' select={false} columns={columns} />
                )}
            </Column>
        </>
    );
};
